import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Loader2, AlertCircle, CheckCircle, Search, X, Download } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ValueSetterParams,
  CellClassParams,
  ICellRendererParams,
  GridReadyEvent,
  RowClassParams
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Logo from '@/components/Logo';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import apiClient from '@/services/api/client';

// Row data interface for the grid
interface MenuRowData {
  menu_item_id: string | null; // null for new items
  section: string;
  section_id: string;
  item_name: string;
  item_description: string;
  price: number;
  is_market_price: boolean;
  vegetarian: boolean;
  vegan: boolean;
  gluten_free: boolean;
  spice_adjustable: boolean;
  is_popular: boolean;
  // Tracking fields
  _isNew?: boolean;
  _isModified?: boolean;
  _isDeleted?: boolean;
  _originalData?: MenuRowData;
}

interface MenuSection {
  id: string;
  name: string;
}

const MenuTableEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const gridRef = useRef<AgGridReact>(null);

  // URL params
  const processingId = searchParams.get('processing_id') || searchParams.get('menu_id');
  const restId = searchParams.get('rest_id');

  // State
  const [rowData, setRowData] = useState<MenuRowData[]>([]);
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [saveResults, setSaveResults] = useState<{ added: number; updated: number; errors: string[] } | null>(null);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [selectedSectionForNewItem, setSelectedSectionForNewItem] = useState<string>('');

  // Track changes
  const checkForChanges = useCallback((data: MenuRowData[]) => {
    const hasAnyChanges = data.some(row => row._isNew || row._isModified || row._isDeleted);
    setHasChanges(hasAnyChanges);
  }, []);

  // Fetch menu data
  const fetchMenuData = useCallback(async () => {
    if (!processingId) {
      toast({ title: 'Error', description: 'No menu ID provided', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get(`/menus/${processingId}/items`);
      const data = response.data;

      // Extract sections
      const sectionsList: MenuSection[] = data.sections.map((s: any) => ({
        id: s.id,
        name: s.name
      }));
      setSections(sectionsList);

      // Transform items to row data
      const rows: MenuRowData[] = data.items.map((item: any) => ({
        menu_item_id: item.id,
        section: item.section || sectionsList.find(s => s.id === item.section_id)?.name || 'Unknown',
        section_id: item.section_id,
        item_name: item.name,
        item_description: item.description || '',
        price: item.price || 0,
        is_market_price: item.is_market_price || false,
        vegetarian: item.dietary_flags?.vegetarian || false,
        vegan: item.dietary_flags?.vegan || false,
        gluten_free: item.dietary_flags?.gluten_free || false,
        spice_adjustable: item.spice_adjustable || false,
        is_popular: item.is_popular || false,
        _isNew: false,
        _isModified: false,
        _isDeleted: false,
        _originalData: null
      }));

      // Store original data for comparison
      rows.forEach(row => {
        row._originalData = { ...row, _originalData: undefined };
      });

      setRowData(rows);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching menu data:', error);
      toast({ title: 'Error', description: 'Failed to load menu data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [processingId, toast]);

  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // Value setter for editable cells
  const createValueSetter = (field: keyof MenuRowData) => (params: ValueSetterParams<MenuRowData>) => {
    const oldValue = params.data[field];
    const newValue = params.newValue;

    if (oldValue === newValue) return false;

    // Update the value
    (params.data as any)[field] = newValue;

    // Mark as modified if not new
    if (!params.data._isNew) {
      params.data._isModified = true;
    }

    // Update state
    setRowData(prev => {
      const newData = [...prev];
      checkForChanges(newData);
      return newData;
    });

    return true;
  };

  // Column definitions
  const columnDefs = useMemo<ColDef<MenuRowData>[]>(() => [
    {
      headerName: '',
      field: 'menu_item_id',
      width: 50,
      cellRenderer: (params: ICellRendererParams<MenuRowData>) => {
        if (params.data?._isNew) return '🆕';
        if (params.data?._isModified) return '✏️';
        if (params.data?._isDeleted) return '🗑️';
        return '';
      },
      editable: false,
      sortable: false,
      filter: false
    },
    {
      headerName: 'Section',
      field: 'section',
      width: 150,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: sections.map(s => s.name)
      },
      valueSetter: (params: ValueSetterParams<MenuRowData>) => {
        const newSection = params.newValue;
        const section = sections.find(s => s.name === newSection);
        if (section) {
          params.data.section = newSection;
          params.data.section_id = section.id;
          if (!params.data._isNew) {
            params.data._isModified = true;
          }
          setRowData(prev => {
            const newData = [...prev];
            checkForChanges(newData);
            return newData;
          });
          return true;
        }
        return false;
      }
    },
    {
      headerName: 'Item Name',
      field: 'item_name',
      flex: 2,
      minWidth: 200,
      editable: true,
      valueSetter: createValueSetter('item_name')
    },
    {
      headerName: 'Description',
      field: 'item_description',
      flex: 3,
      minWidth: 300,
      editable: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      valueSetter: createValueSetter('item_description')
    },
    {
      headerName: 'Price',
      field: 'price',
      width: 100,
      editable: true,
      valueFormatter: (params) => {
        if (params.data?.is_market_price) return 'MKT';
        return params.value ? `$${Number(params.value).toFixed(2)}` : '$0.00';
      },
      valueSetter: createValueSetter('price')
    },
    {
      headerName: 'MKT',
      field: 'is_market_price',
      width: 70,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueSetter: createValueSetter('is_market_price')
    },
    {
      headerName: 'V',
      field: 'vegetarian',
      width: 70,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueSetter: createValueSetter('vegetarian'),
      headerTooltip: 'Vegetarian'
    },
    {
      headerName: 'VG',
      field: 'vegan',
      width: 70,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueSetter: createValueSetter('vegan'),
      headerTooltip: 'Vegan'
    },
    {
      headerName: 'GF',
      field: 'gluten_free',
      width: 70,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueSetter: createValueSetter('gluten_free'),
      headerTooltip: 'Gluten Free'
    },
    {
      headerName: '🌶️',
      field: 'spice_adjustable',
      width: 70,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueSetter: createValueSetter('spice_adjustable'),
      headerTooltip: 'Spice Adjustable'
    },
    {
      headerName: '⭐',
      field: 'is_popular',
      width: 70,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueSetter: createValueSetter('is_popular'),
      headerTooltip: 'Popular Item'
    },
    {
      headerName: '',
      width: 60,
      cellRenderer: (params: ICellRendererParams<MenuRowData>) => {
        return (
          <button
            onClick={() => handleDeleteRow(params.data!)}
            className="p-1 text-red-500 hover:text-red-700"
            title="Delete item"
          >
            <Trash2 size={16} />
          </button>
        );
      },
      editable: false,
      sortable: false,
      filter: false
    }
  ], [sections, checkForChanges]);

  // Default column settings - no individual filters for cleaner look
  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: false,  // Disabled individual column filters
    resizable: true
  }), []);

  // Global search filter
  const [quickFilterText, setQuickFilterText] = useState('');

  // Row class for styling modified/new/deleted rows
  const getRowClass = useCallback((params: RowClassParams<MenuRowData>) => {
    if (params.data?._isDeleted) return 'bg-red-100 line-through opacity-50';
    if (params.data?._isNew) return 'bg-green-50';
    if (params.data?._isModified) return 'bg-yellow-50';
    return '';
  }, []);

  // Open add item dialog
  const handleAddRow = useCallback(() => {
    if (sections.length === 0) {
      toast({ title: 'Error', description: 'No sections available', variant: 'destructive' });
      return;
    }
    setSelectedSectionForNewItem('');
    setShowAddItemDialog(true);
  }, [sections, toast]);

  // Actually add the new row after section selection
  const confirmAddRow = useCallback(() => {
    const section = sections.find(s => s.id === selectedSectionForNewItem);
    if (!section) {
      toast({ title: 'Error', description: 'Please select a section', variant: 'destructive' });
      return;
    }

    const newRow: MenuRowData = {
      menu_item_id: null,
      section: section.name,
      section_id: section.id,
      item_name: '',
      item_description: '',
      price: 0,
      is_market_price: false,
      vegetarian: false,
      vegan: false,
      gluten_free: false,
      spice_adjustable: false,
      is_popular: false,
      _isNew: true,
      _isModified: false,
      _isDeleted: false
    };

    setRowData(prev => {
      const newData = [...prev, newRow];
      checkForChanges(newData);
      return newData;
    });

    setShowAddItemDialog(false);

    // Focus on the new row
    setTimeout(() => {
      if (gridRef.current?.api) {
        const lastRowIndex = rowData.length;
        gridRef.current.api.ensureIndexVisible(lastRowIndex);
        gridRef.current.api.setFocusedCell(lastRowIndex, 'item_name');
        gridRef.current.api.startEditingCell({
          rowIndex: lastRowIndex,
          colKey: 'item_name'
        });
      }
    }, 100);
  }, [sections, selectedSectionForNewItem, rowData.length, checkForChanges, toast]);

  // Delete row (mark as deleted)
  const handleDeleteRow = useCallback((data: MenuRowData) => {
    setRowData(prev => {
      const newData = prev.map(row => {
        if (row === data) {
          if (row._isNew) {
            // Remove new rows entirely
            return null;
          }
          // Mark existing rows as deleted
          return { ...row, _isDeleted: !row._isDeleted };
        }
        return row;
      }).filter(Boolean) as MenuRowData[];

      checkForChanges(newData);
      return newData;
    });
  }, [checkForChanges]);

  // Save all changes
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveResults(null);

    const results = { added: 0, updated: 0, deleted: 0, errors: [] as string[] };

    try {
      // Get all changes
      const newRows = rowData.filter(r => r._isNew && !r._isDeleted);
      const modifiedRows = rowData.filter(r => r._isModified && !r._isNew && !r._isDeleted);
      const deletedRows = rowData.filter(r => r._isDeleted && !r._isNew);

      // Validate new rows
      for (const row of newRows) {
        if (!row.item_name.trim()) {
          results.errors.push(`New item in ${row.section}: Item name is required`);
        }
        if (row.price <= 0 && !row.is_market_price) {
          results.errors.push(`${row.item_name || 'New item'}: Price is required (or mark as Market Price)`);
        }
      }

      if (results.errors.length > 0) {
        setSaveResults(results);
        setSaving(false);
        return;
      }

      // Process deletions
      for (const row of deletedRows) {
        try {
          await apiClient.delete(`/menus/${processingId}/items/${row.menu_item_id}`);
          results.deleted++;
        } catch (error: any) {
          results.errors.push(`Failed to delete ${row.item_name}: ${error.message}`);
        }
      }

      // Process updates
      for (const row of modifiedRows) {
        try {
          await apiClient.put(`/menus/${processingId}/items/${row.menu_item_id}`, {
            name: row.item_name,
            description: row.item_description,
            section: row.section,
            price: row.price,
            is_market_price: row.is_market_price,
            market_price_indicator: row.is_market_price ? 'MKT' : null,
            dietary_flags: {
              vegetarian: row.vegetarian,
              vegan: row.vegan,
              gluten_free: row.gluten_free
            },
            allergens: [],
            spice_adjustable: row.spice_adjustable,
            is_popular: row.is_popular
          });
          results.updated++;
        } catch (error: any) {
          results.errors.push(`Failed to update ${row.item_name}: ${error.message}`);
        }
      }

      // Process new items
      for (const row of newRows) {
        try {
          await apiClient.post(`/menus/${processingId}/items`, {
            name: row.item_name,
            description: row.item_description,
            section_id: row.section_id,
            section_name: row.section,
            price: row.price,
            is_market_price: row.is_market_price,
            market_price_indicator: row.is_market_price ? 'MKT' : null,
            dietary_flags: {
              vegetarian: row.vegetarian,
              vegan: row.vegan,
              gluten_free: row.gluten_free
            },
            allergens: [],
            spice_adjustable: row.spice_adjustable,
            is_popular: row.is_popular
          });
          results.added++;
        } catch (error: any) {
          results.errors.push(`Failed to add ${row.item_name}: ${error.message}`);
        }
      }

      setSaveResults(results);

      if (results.errors.length === 0) {
        toast({
          title: 'Success',
          description: `Saved: ${results.added} added, ${results.updated} updated, ${results.deleted} deleted`,
        });
        // Refresh data
        await fetchMenuData();
      } else {
        toast({
          title: 'Partial Success',
          description: `Some items saved, ${results.errors.length} errors occurred`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: 'Error', description: 'Failed to save changes', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [rowData, processingId, toast, fetchMenuData]);

  // Handle navigation with unsaved changes
  const handleBack = useCallback(() => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
      setPendingNavigation(`/restaurant-menu-review?processing_id=${processingId}&rest_id=${restId}`);
    } else {
      navigate(`/restaurant-menu-review?processing_id=${processingId}&rest_id=${restId}`);
    }
  }, [hasChanges, navigate, processingId, restId]);

  const confirmNavigation = useCallback(() => {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  }, [navigate, pendingNavigation]);

  // Download menu as CSV
  const downloadCSV = useCallback(() => {
    // Filter out deleted rows for export
    const activeRows = rowData.filter(row => !row._isDeleted);

    if (activeRows.length === 0) {
      toast({ title: 'No Data', description: 'No menu items to export', variant: 'destructive' });
      return;
    }

    // CSV headers
    const headers = ['Section', 'Item Name', 'Description', 'Price', 'Market Price', 'Vegetarian', 'Vegan', 'Gluten Free', 'Spice Adjustable', 'Popular'];

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...activeRows.map(row => [
        `"${(row.section || '').replace(/"/g, '""')}"`,
        `"${(row.item_name || '').replace(/"/g, '""')}"`,
        `"${(row.item_description || '').replace(/"/g, '""')}"`,
        row.is_market_price ? 'Market Price' : `$${row.price?.toFixed(2) || '0.00'}`,
        row.is_market_price ? 'Yes' : 'No',
        row.vegetarian ? 'Yes' : 'No',
        row.vegan ? 'Yes' : 'No',
        row.gluten_free ? 'Yes' : 'No',
        row.spice_adjustable ? 'Yes' : 'No',
        row.is_popular ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `menu-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({ title: 'Downloaded', description: `Exported ${activeRows.length} menu items to CSV` });
  }, [rowData, toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dine-purple/10 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-dine-purple" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-dine-purple/10 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          {/* Left: Navigation */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Logo size="sm" />
            <h1 className="text-lg font-semibold text-gray-800">Table Editor</h1>
          </div>

          {/* Center: Search */}
          <div className="relative flex-1 max-w-md mx-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search menu items..."
              value={quickFilterText}
              onChange={(e) => setQuickFilterText(e.target.value)}
              className="pl-9 pr-8 w-full h-9"
            />
            {quickFilterText && (
              <button
                onClick={() => setQuickFilterText('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="text-sm text-amber-600 flex items-center gap-1 whitespace-nowrap">
                <AlertCircle className="w-4 h-4" />
                Unsaved
              </span>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-purple-600 hover:bg-purple-700 text-white disabled:bg-purple-300 disabled:text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddRow}
              disabled={saving}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCSV}
              disabled={saving || rowData.length === 0}
              title="Download menu as CSV"
            >
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
          </div>
        </div>
      </header>

      {/* Results banner */}
      {saveResults && (
        <div className={`px-4 py-2 ${saveResults.errors.length > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
          <div className="max-w-[1600px] mx-auto flex items-center gap-2">
            {saveResults.errors.length > 0 ? (
              <>
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700">
                  {saveResults.errors.length} error(s): {saveResults.errors[0]}
                </span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-700">
                  Successfully saved: {saveResults.added} added, {saveResults.updated} updated
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="p-4">
        <div className="max-w-[1600px] mx-auto">
          <div
            className="ag-theme-alpine"
            style={{ height: 'calc(100vh - 160px)', width: '100%' }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowClass={getRowClass}
              quickFilterText={quickFilterText}
              animateRows={true}
              singleClickEdit={true}
              stopEditingWhenCellsLoseFocus={true}
              undoRedoCellEditing={true}
              undoRedoCellEditingLimit={20}
              enableCellTextSelection={true}
            />
          </div>

          {/* Legend */}
          <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-green-50 border rounded"></span>
              New item
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-yellow-50 border rounded"></span>
              Modified
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-red-100 border rounded"></span>
              Deleted
            </span>
            <span className="text-gray-400">|</span>
            <span>V = Vegetarian, VG = Vegan, GF = Gluten Free, MKT = Market Price, 🌶️ = Spice Adjustable, ⭐ = Popular</span>
          </div>
        </div>
      </div>

      {/* Unsaved changes dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnsavedDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmNavigation}>
              Leave without saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item dialog - Section selection */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Select Section
            </label>
            <Select
              value={selectedSectionForNewItem}
              onValueChange={setSelectedSectionForNewItem}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a section..." />
              </SelectTrigger>
              <SelectContent>
                {sections.map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {section.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAddRow}
              disabled={!selectedSectionForNewItem}
              className="bg-dine-purple hover:bg-dine-purple/90"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuTableEditor;
